import { useEffect, useState } from 'react';
import { api } from '../services/api';

const cache = {};
const inflight = {};

async function loadPage(pageKey, force = false) {
  if (inflight[pageKey]) return inflight[pageKey];
  if (!force && cache[pageKey]) return cache[pageKey];

  inflight[pageKey] = api.getContent(pageKey)
    .then((res) => {
      cache[pageKey] = res.data || {};
      return cache[pageKey];
    })
    .catch(() => {
      if (!cache[pageKey]) cache[pageKey] = {};
      return cache[pageKey];
    })
    .finally(() => {
      delete inflight[pageKey];
    });

  return inflight[pageKey];
}

/** Clear cache after CMS edits (optional; hard refresh also works). */
export function clearSiteContentCache(pageKey) {
  if (pageKey) delete cache[pageKey];
  else Object.keys(cache).forEach((k) => delete cache[k]);
}

/**
 * Load CMS page content blocks: { section: { field: value } }
 * Refetches on every mount so CMS image/map edits show after navigating back.
 */
export function useSiteContent(pageKey = 'home') {
  const [content, setContent] = useState(() => cache[pageKey] || {});
  const [loading, setLoading] = useState(!cache[pageKey]);

  useEffect(() => {
    let cancelled = false;
    if (cache[pageKey]) {
      setContent(cache[pageKey]);
      setLoading(false);
    } else {
      setLoading(true);
    }
    loadPage(pageKey, true).then((data) => {
      if (!cancelled) {
        setContent(data);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [pageKey]);

  return { content, loading };
}

export function sectionField(content, section, field, fallback = '') {
  const value = content?.[section]?.[field];
  if (value === undefined || value === null || value === '') return fallback;
  return value;
}

export default useSiteContent;
